import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'next-i18next';
import { Suspense } from 'react';

import type { MyProfileResponse } from '../../../../../../resources/@types/accountSchema';
import useClientAccountMyProfileQuery from '../../../../../queries/useClientAccountMyProfileQuery';
import { DeferredComponent } from '../../../../util/DeferredComponent';
import { SettingSection } from '../common/SettingSection';
import { SectionSkeleton } from './SectionSkeleton';
import { EditProfile } from './profile/Edit';
import { ReadOnlyProfile } from './profile/ReadOnly';

export const ProfileSection = () => {
  const { t } = useTranslation();

  return (
    <SettingSection title={t('내 프로필')}>
      <Suspense
        fallback={
          <DeferredComponent>
            <SectionSkeleton lines={3} />
          </DeferredComponent>
        }
      >
        <ProfileSectionItem />
      </Suspense>
    </SettingSection>
  );
};

const ProfileSectionItem = () => {
  const { data: profile } = useClientAccountMyProfileQuery({
    suspense: true,
    refetchOnWindowFocus: false,
  });
  const [isEditMode, { open: openEditMode, close: closeEditMode }] = useDisclosure(false);

  return (
    <>
      {isEditMode && <EditProfile profile={safeProfile(profile)} onClose={closeEditMode} />}
      {!isEditMode && (
        <ReadOnlyProfile profile={safeProfile(profile)} openEditMode={openEditMode} />
      )}
    </>
  );
};

export const DEFAULT_PROFILE_VALUES: Readonly<MyProfileResponse> = {
  profileImageFileId: null,
  profileImageUrl: '',
  id: 0,
  nickname: '',
  slug: '',
  introduce: '',
} as const;

const isProfileType = (profile?: MyProfileResponse | null): profile is MyProfileResponse =>
  !!profile;

const safeProfile = (profile?: MyProfileResponse | null): MyProfileResponse =>
  isProfileType(profile) ? profile : DEFAULT_PROFILE_VALUES;
